import React from 'react';
import { connect } from 'react-redux';
import { ThemeProvider } from 'styled-components';
import {
  changeBulkLearnerActivityUploadPage,
  ChangeBulkLearnerActivityUploadPageActionCreator,
} from 'src/ducks/bulkLearnerActivityUploads';
import { Borders } from '../../design_system/src/tokens/Borders';
import { Colors } from '../../design_system/src/tokens/Colors';
import { Fonts } from '../../design_system/src/tokens/Fonts';
import { Shadows } from '../../design_system/src/tokens/Shadows';
import { Spacing } from '../../design_system/src/tokens/Spacing';
import { ReduxStore } from '../../ducks';
import {
  bulkLearnerActivityUploadDataSelector,
  bulkLearnerActivityUploadErrorSelector,
  bulkLearnerActivityUploadPageSelector,
  bulkLearnerActivityUploadPageSizeSelector,
  bulkLearnerActivityUploadTotalSelector,
} from '../../selectors/bulkLearnerActivityUploads';
import styled from '../../theme';
import { themeColor } from '../../theme/helpers';
import { Color } from '../../theme/primaryTheme';
import { BulkLearnerActivityUpload } from '../../types';
import { momentUtc } from '../../utilities/moment';
import Alert, { AlertType } from '../Alert';
import { FetchBulkLearnerActivityUploads } from '../FetchBulkLearnerActivities';
import Pagination from '../../design_system/src/web/molecules/Pagination';
import Table, { ColumnConfig } from '../Table';
import Text, { TextStyle } from '../Text';
import { TextStack } from '../TextStack';
import BulkLearnerActivityUploadIngestionReportDownloadButton from './BulkLearnerActivityUploadIngestionReportDownloadButton';
import BulkLearnerActivityUploadOriginalFileDownloadButton from './BulkLearnerActivityUploadOriginalFileDownloadButton';

const dsTheme = {
  name: 'AMA',
  colors: Colors,
  borders: Borders,
  fonts: Fonts,
  spacing: Spacing,
  shadows: Shadows,
  inputs: { height: '44px', fontSize: '1.6rem' },
  textareas: { height: '100px' },
};

interface Props {
  uploads: BulkLearnerActivityUpload[];
  page: number;
  pageSize: number;
  total: number;
  error?: any;
  changeBulkLearnerActivityUploadPage?: ChangeBulkLearnerActivityUploadPageActionCreator;
  startDate?: string;
  endDate?: string;
}

const BulkLearnerActivityUploadsTable: React.FunctionComponent<Props> = ({
  uploads,
  error,
  page,
  pageSize,
  total,
  changeBulkLearnerActivityUploadPage,
  startDate,
  endDate,
}) => {
  const columns: ColumnConfig[] = [
    {
      header: 'Date/Time Submitted',
      id: 'createdAt',
      accessor: (upload: BulkLearnerActivityUpload) => <Text>{momentUtc(upload.createdAt).local().format('M/D/YYYY HH:mm')}</Text>,
    },
    {
      header: 'File Name',
      id: 'originalFileName',
      accessor: (upload: BulkLearnerActivityUpload) => `/v1/bulk-learner-activity-uploads/${upload.id}/original-file-data`,
      cellComponent: BulkLearnerActivityUploadOriginalFileDownloadButton,
    },
    {
      header: 'Uploaded By',
      id: 'createdByUserName',
      accessor: (upload: BulkLearnerActivityUpload) => <Text>{upload.createdByUserName}</Text>,
    },
    {
      header: 'Ingestion Report',
      id: 'ingestionReport',
      accessor: (upload: BulkLearnerActivityUpload) => `/v1/bulk-learner-activity-uploads/${upload.id}/ingestion-report-data`,
      cellComponent: BulkLearnerActivityUploadIngestionReportDownloadButton,
    },
  ];

  const totalPages = total && pageSize ? Math.ceil(total / pageSize) : 0;
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages && changeBulkLearnerActivityUploadPage) {
      changeBulkLearnerActivityUploadPage(newPage);
    }
  };

  return (
    <>
      <FetchBulkLearnerActivityUploads startDate={startDate} endDate={endDate}>
        {error && (
          <StyledAlert data-test-id="uploadTableError" type={AlertType.Error}>
            {error.message}
          </StyledAlert>
        )}
        {uploads.length > 0 ? (
          <StyledTable id="uploadTable" columns={columns} data={uploads} />
        ) : (
          !error && (
            <EmptyStateRoot>
              <TextStack>
                <Text textStyle={TextStyle.SubHeadline} color={Color.Primary} bold={true}>
                  No matches found.
                </Text>
                <Text textStyle={TextStyle.Medium}>Try adjusting the date range to find uploads.</Text>
              </TextStack>
            </EmptyStateRoot>
          )
        )}
      </FetchBulkLearnerActivityUploads>
      {totalPages > 1 && (
        <ThemeProvider theme={dsTheme}>
          <Pagination currentPage={page || 1} pageCount={totalPages} onPageChanged={handlePageChange} />
        </ThemeProvider>
      )}
    </>
  );
};

const StyledAlert = styled(Alert)`
  margin-top: 2rem;
  margin-bottom: 2rem;
`;

const StyledTable = styled(Table)`
  margin-top: 2rem;
`;

const EmptyStateRoot = styled.div`
  margin-top: 2rem;
  text-align: center;
  background-color: ${themeColor(Color.Reverse)};
  padding: 4rem;
  border-radius: 0.4rem;
`;

const mapStateToProps = (state: ReduxStore) => ({
  uploads: bulkLearnerActivityUploadDataSelector(state),
  pageSize: bulkLearnerActivityUploadPageSizeSelector(state),
  page: bulkLearnerActivityUploadPageSelector(state),
  total: bulkLearnerActivityUploadTotalSelector(state),
  error: bulkLearnerActivityUploadErrorSelector(state),
});

const mapDispatchToProps = {
  changeBulkLearnerActivityUploadPage,
};

export default connect(mapStateToProps, mapDispatchToProps)(BulkLearnerActivityUploadsTable);
